import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form, Header, Label, Modal, Pagination, Segment, Select, Table } from 'semantic-ui-react';
import { API, isRoot, showError, showSuccess, timestamp2string } from '../helpers';

import { ITEMS_PER_PAGE } from '../constants';
import { renderQuota } from '../helpers/render';

function formatTokenCount(count) {
  if (count > 1000) {
    return (count / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  }
  return count;
}

function renderTimestamp(timestamp) {
  return (
    <>
      {timestamp2string(timestamp)}
    </>
  );
}

const MODE_OPTIONS = [
  { key: 'all', text: '全部用户', value: 'all' },
  { key: 'self', text: '当前用户', value: 'self' }
];

const LOG_OPTIONS = [
  { key: 'all', text: '全部', value: '' },
  { key: 'consume', text: '消费', value: 'consume' },
  { key: 'topup', text: '充值', value: 'topup' },
  { key: 'manage', text: '管理', value: 'manage' },
  { key: 'system', text: '系统', value: 'system' }
];

const LOG_OPTIONS_USER = [
  { key: 'consume', text: '消费', value: 'consume' },
  { key: 'topup', text: '充值', value: 'topup' }
];

function renderType(type) {
  switch (type) {
    case 1:
      return <Label basic color='green'> 充值 </Label>;
    case 2:
    case 5:
    case 6:
      return <Label basic color='olive'> 消费 </Label>;
    case 3:
      return <Label basic color='orange'> 管理 </Label>;
    case 4:
      return <Label basic color='purple'> 系统 </Label>;
    case 7:
      return <Label basic color='red'> 错误 </Label>;
    default:
      return <Label basic color='black'> 未知 </Label>;
  }
}

const withIn = 32 * 24 * 3600

// With responseType 'blob' an error response body is a Blob too, so the usual
// e.response.data.message is unreadable — parse it back into text first.
async function readBlobError(e) {
  const data = e?.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = JSON.parse(text);
      if (parsed?.message) return parsed.message;
    } catch (_) {
      // not JSON, fall through
    }
  }
  if (data?.message) return data.message;
  return e?.message || '未知错误';
}

function triggerBlobDownload(res, fallbackName) {
  const disposition = res.headers['content-disposition'] || '';
  let filename = fallbackName;
  const matchStar = disposition.match(/filename\*=UTF-8''([^\s;]+)/);
  if (matchStar) {
    filename = decodeURIComponent(matchStar[1]);
  } else {
    const match = disposition.match(/filename=([^\s;]+)/);
    if (match) filename = match[1];
  }
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const LogsTable = () => {
  const [logs, setLogs] = useState([]);
  const [showStat, setShowStat] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState(1);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searching, setSearching] = useState(false);
  const [logType, setLogType] = useState('consume');
  const isAdminUser = isRoot();
  let now = new Date();
  const [inputs, setInputs] = useState({
    username: '',
    token_name: '',
    model_name: '',
    start_timestamp: timestamp2string(now.getTime() / 1000 - withIn),
    end_timestamp: timestamp2string(now.getTime() / 1000 + 3600),
    channel: '',
    upstream_id: ''
  });
  const { username, token_name, model_name, start_timestamp, end_timestamp, channel, upstream_id } = inputs;

  const [stat, setStat] = useState({
    quota: 0,
    token: 0
  });

  const [exportModal, setExportModal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const nowForExport = new Date();
  const [exportInputs, setExportInputs] = useState({
    export_start: timestamp2string(nowForExport.getTime() / 1000 - 7 * 24 * 3600),
    export_end: timestamp2string(nowForExport.getTime() / 1000)
  });
  const { export_start, export_end } = exportInputs;

  const [exportTopupModal, setExportTopupModal] = useState(false);
  const [exportingTopup, setExportingTopup] = useState(false);
  const [exportTopupError, setExportTopupError] = useState('');
  const [exportTopupInputs, setExportTopupInputs] = useState({
    export_topup_start: timestamp2string(nowForExport.getTime() / 1000 - 7 * 24 * 3600),
    export_topup_end: timestamp2string(nowForExport.getTime() / 1000)
  });
  const { export_topup_start, export_topup_end } = exportTopupInputs;

  const [detailContent, setDetailContent] = useState(null);

  const prettyDetailContent = useMemo(() => {
    if (detailContent === null) return '';
    try {
      return JSON.stringify(JSON.parse(detailContent), null, 2);
    } catch (_) {
      return detailContent;
    }
  }, [detailContent]);

  const handleExportInputChange = (e, { name, value }) => {
    setExportInputs((prev) => ({ ...prev, [name]: value }));
    setExportError('');
  };

  const handleExportTopupInputChange = (e, { name, value }) => {
    setExportTopupInputs((prev) => ({ ...prev, [name]: value }));
    setExportTopupError('');
  };

  const handleExport = async () => {
    const startTs = Math.floor(Date.parse(export_start) / 1000);
    const endTs = Math.floor(Date.parse(export_end) / 1000);
    if (!startTs || !endTs || endTs <= startTs) {
      setExportError('请选择有效的时间范围');
      return;
    }
    if (endTs - startTs > 30 * 24 * 3600) {
      setExportError('时间范围不能超过 30 天');
      return;
    }
    setExporting(true);
    setExportError('');
    try {
      const res = await API.get(`/api/log/self/export?start_timestamp=${startTs}&end_timestamp=${endTs}`, {
        responseType: 'blob'
      });
      triggerBlobDownload(res, 'mlog.csv');
      setExportModal(false);
    } catch (e) {
      setExportError('导出失败：' + (await readBlobError(e)));
    }
    setExporting(false);
  };

  const handleExportTopup = async () => {
    const startTs = Math.floor(Date.parse(export_topup_start) / 1000);
    const endTs = Math.floor(Date.parse(export_topup_end) / 1000);
    if (!startTs || !endTs || endTs <= startTs) {
      setExportTopupError('请选择有效的时间范围');
      return;
    }
    if (endTs - startTs > 30 * 24 * 3600) {
      setExportTopupError('时间范围不能超过 30 天');
      return;
    }
    setExportingTopup(true);
    setExportTopupError('');
    try {
      const res = await API.get(`/api/log/self/export_topup?start_timestamp=${startTs}&end_timestamp=${endTs}`, {
        responseType: 'blob'
      });
      triggerBlobDownload(res, 'topup.csv');
      setExportTopupModal(false);
    } catch (e) {
      setExportTopupError('导出失败：' + (await readBlobError(e)));
    }
    setExportingTopup(false);
  };

  const handleInputChange = (e, { name, value }) => {
    setInputs((inputs) => ({ ...inputs, [name]: value }));
  };

  // Admin-only: export every log matching the current query form, not just the
  // page of rows currently rendered.
  const [exportingAdmin, setExportingAdmin] = useState(false);

  const handleExportAdmin = async () => {
    const startTs = Math.floor(Date.parse(start_timestamp) / 1000);
    const endTs = Math.floor(Date.parse(end_timestamp) / 1000);
    if (!startTs || !endTs || endTs <= startTs) {
      showError('请选择有效的时间范围');
      return;
    }
    if (endTs - startTs > 90 * 24 * 3600) {
      showError('时间范围不能超过 90 天');
      return;
    }
    setExportingAdmin(true);
    try {
      const params = new URLSearchParams({
        start_timestamp: startTs,
        end_timestamp: endTs,
        type: logType,
        username,
        token_name,
        model_name,
        channel,
        upstream_id
      }).toString();
      const res = await API.get(`/api/log/admin/export?${params}`, {
        responseType: 'blob'
      });
      triggerBlobDownload(res, 'admin-mlog.csv');
    } catch (e) {
      showError('导出失败：' + (await readBlobError(e)));
    }
    setExportingAdmin(false);
  };

  const getLogSelfStat = async () => {
    let localStartTimestamp = Date.parse(start_timestamp) / 1000;
    let localEndTimestamp = Date.parse(end_timestamp) / 1000;
    let res = await API.get(`/api/log/self/stat?type=${logType}&token_name=${token_name}&model_name=${model_name}&start_timestamp=${localStartTimestamp}&end_timestamp=${localEndTimestamp}`);
    const { success, message, data } = res.data;
    if (success) {
      setStat(data);
    } else {
      showError(message);
    }
  };

  const getLogStat = async () => {
    let localStartTimestamp = Date.parse(start_timestamp) / 1000;
    let localEndTimestamp = Date.parse(end_timestamp) / 1000;
    let res = await API.get(`/api/log/stat?type=${logType}&username=${username}&token_name=${token_name}&model_name=${model_name}&start_timestamp=${localStartTimestamp}&end_timestamp=${localEndTimestamp}&channel=${channel}`);
    const { success, message, data } = res.data;
    if (success) {
      setStat(data);
    } else {
      showError(message);
    }
  };

  const handleEyeClick = async () => {
    if (!showStat) {
      if (isAdminUser) {
        await getLogStat();
      } else {
        await getLogSelfStat();
      }
    }
    setShowStat(!showStat);
  };

  const loadLogs = async (startIdx) => {
    let url = '';
    let localStartTimestamp = Date.parse(start_timestamp) / 1000;
    let localEndTimestamp = Date.parse(end_timestamp) / 1000;
    if (isAdminUser) {
      url = `/api/log/?p=${startIdx}&type=${logType}&username=${username}&token_name=${token_name}&model_name=${model_name}&start_timestamp=${localStartTimestamp}&end_timestamp=${localEndTimestamp}&channel=${channel}&upstream_id=${encodeURIComponent(upstream_id)}`;
    } else {
      url = `/api/log/self/?p=${startIdx}&type=${logType}&token_name=${token_name}&model_name=${model_name}&start_timestamp=${localStartTimestamp}&end_timestamp=${localEndTimestamp}&upstream_id=${encodeURIComponent(upstream_id)}`;
    }
    const res = await API.get(url);
    const { success, message, data } = res.data;
    if (success) {
      if (startIdx === 0) {
        setLogs(data);
      } else {
        let newLogs = [...logs];
        newLogs.splice(startIdx * ITEMS_PER_PAGE, data.length, ...data);
        setLogs(newLogs);
      }
    } else {
      showError(message);
    }
    setLoading(false);
  };

  const onPaginationChange = (e, { activePage }) => {
    (async () => {
      if (activePage === Math.ceil(logs.length / ITEMS_PER_PAGE) + 1) {
        // In this case we have to load more data and then append them.
        await loadLogs(activePage - 1);
      }
      setActivePage(activePage);
    })();
  };

  const refresh = async () => {
    setLoading(true);
    setActivePage(1);
    await loadLogs(0);
  };

  useEffect(() => {
    refresh().then();
  }, [logType]);

  const searchLogs = async () => {
    if (searchKeyword === '') {
      // if keyword is blank, load files instead.
      await loadLogs(0);
      setActivePage(1);
      return;
    }
    setSearching(true);
    const res = await API.get(`/api/log/self/search?keyword=${searchKeyword}`);
    const { success, message, data } = res.data;
    if (success) {
      setLogs(data);
      setActivePage(1);
    } else {
      showError(message);
    }
    setSearching(false);
  };

  const handleKeywordChange = async (e, { value }) => {
    setSearchKeyword(value.trim());
  };

  const sortLog = (key) => {
    if (logs.length === 0) return;
    setLoading(true);
    let sortedLogs = [...logs];
    if (typeof sortedLogs[0][key] === 'string') {
      sortedLogs.sort((a, b) => {
        return ('' + a[key]).localeCompare(b[key]);
      });
    } else {
      sortedLogs.sort((a, b) => {
        if (a[key] === b[key]) return 0;
        if (a[key] > b[key]) return -1;
        if (a[key] < b[key]) return 1;
      });
    }
    if (sortedLogs[0].id === logs[0].id) {
      sortedLogs.reverse();
    }
    setLogs(sortedLogs);
    setLoading(false);
  };

  return (
    <>
      <Segment>
        <Header as='h3'>
          使用明细
          {/* （总消耗额度： */}
          {/* {showStat && renderQuota(stat.quota)} */}
          {/* {!showStat && <span onClick={handleEyeClick} style={{ cursor: 'pointer', color: 'gray' }}>点击查看</span>} */}
          {/* ） */}
        </Header>
        <Form>
          <Form.Group>
            <Form.Input fluid label={'令牌名称'} width={3} value={token_name}
                        placeholder={'可选值'} name='token_name' onChange={handleInputChange} />
            <Form.Input fluid label='模型名称' width={3} value={model_name} placeholder='可选值'
                        name='model_name'
                        onChange={handleInputChange} />
            <Form.Input fluid label='上游ID' width={3} value={upstream_id} placeholder='精确匹配'
                        name='upstream_id'
                        onChange={handleInputChange} />
            <Form.Input fluid label='起始时间' width={4} value={start_timestamp} type='datetime-local'
                        name='start_timestamp'
                        onChange={handleInputChange} />
            <Form.Input fluid label='结束时间' width={4} value={end_timestamp} type='datetime-local'
                        name='end_timestamp'
                        onChange={handleInputChange} />
            <Form.Button fluid label='操作' width={2} onClick={refresh}>查询</Form.Button>
          </Form.Group>
          <Form.Group>
            <Form.Select fluid label='类型' width={3} value={logType}
                         options={isAdminUser ? LOG_OPTIONS : LOG_OPTIONS_USER}
                         onChange={(e, { value }) => setLogType(value)} />
          </Form.Group>
          {
            isAdminUser && <>
              <Form.Group>
                <Form.Input fluid label={'渠道 ID'} width={3} value={channel}
                            placeholder='可选值' name='channel'
                            onChange={handleInputChange} />
                <Form.Input fluid label={'用户名称'} width={3} value={username}
                            placeholder={'可选值'} name='username'
                            onChange={handleInputChange} />
                <Form.Button fluid label='导出' width={2} loading={exportingAdmin}
                             onClick={handleExportAdmin}>导出</Form.Button>

              </Form.Group>
            </>
          }
        </Form>
        <Table basic compact size='small'>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  sortLog('created_time');
                }}
                width={3}
              >
                时间
              </Table.HeaderCell>
              {
                isAdminUser && <Table.HeaderCell
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    sortLog('channel');
                  }}
                  width={1}
                >
                  渠道
                </Table.HeaderCell>
              }
              {
                isAdminUser && <Table.HeaderCell
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    sortLog('username');
                  }}
                  width={1}
                >
                  用户
                </Table.HeaderCell>
              }
              <Table.HeaderCell
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  sortLog('token_name');
                }}
                width={1}
              >
                令牌
              </Table.HeaderCell>
              <Table.HeaderCell
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  sortLog('type');
                }}
                width={2}
              >
                类型
              </Table.HeaderCell>
              <Table.HeaderCell
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  sortLog('model_name');
                }}
                width={2}
              >
                模型
              </Table.HeaderCell>
              <Table.HeaderCell
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  sortLog('prompt_tokens');
                }}
                width={1}
              >
                输入token
              </Table.HeaderCell>
              <Table.HeaderCell
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  sortLog('completion_tokens');
                }}
                width={1}
              >
                输出token
              </Table.HeaderCell>
              <Table.HeaderCell
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  sortLog('quota');
                }}
                width={1}
              >
                消耗额度
              </Table.HeaderCell>
              <Table.HeaderCell
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  sortLog('upstream_id');
                }}
                width={2}
              >
                上游ID
              </Table.HeaderCell>
              <Table.HeaderCell
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  sortLog('content');
                }}
                width={isAdminUser ? 4 : 6}
              >
                详情
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {logs
              .slice(
                (activePage - 1) * ITEMS_PER_PAGE,
                activePage * ITEMS_PER_PAGE
              )
              .map((log, idx) => {
                if (log.deleted) return <></>;
                return (
                  <Table.Row key={log.id}>
                    <Table.Cell>{renderTimestamp(log.created_at)}</Table.Cell>
                    {
                      isAdminUser && (
                        <Table.Cell>{log.channel ? <Label basic>{log.channel}</Label> : ''}</Table.Cell>
                      )
                    }
                    {
                      isAdminUser && (
                        <Table.Cell>{log.username ? <Label>{log.username}</Label> : ''}</Table.Cell>
                      )
                    }
                    <Table.Cell>{log.token_name ? <Label basic>{log.token_name}</Label> : ''}</Table.Cell>
                    <Table.Cell>{renderType(log.type)}</Table.Cell>
                    <Table.Cell>{log.model_name ? <Label basic>{log.model_name}</Label> : ''}</Table.Cell>
                    <Table.Cell>{log.prompt_tokens ? formatTokenCount(log.prompt_tokens) : ''}</Table.Cell>
                    <Table.Cell>{log.completion_tokens ? formatTokenCount(log.completion_tokens) : ''}</Table.Cell>
                    <Table.Cell>{log.quota ? renderQuota(log.quota, 6) : ''}</Table.Cell>
                    <Table.Cell>
                      {log.upstream_id ? (
                        log.upstream_id.length > 16 ? (
                          <Button size='mini' onClick={() => navigator.clipboard.writeText(log.upstream_id).then(() => showSuccess('已复制到剪贴板'))}>复制ID</Button>
                        ) : (
                          <Label basic>{log.upstream_id}</Label>
                        )
                      ) : ''}
                    </Table.Cell>
                    <Table.Cell>
                      {log.content && log.content.length > 50 ? (
                        <>
                          <Button size='mini' onClick={() => setDetailContent(log.content)}>查看详情</Button>
                          {/"video_url"\s*:\s*"([^"]+)"/.test(log.content) && (
                            <Button size='mini' onClick={() => window.open(log.content.match(/"video_url"\s*:\s*"([^"]+)"/)[1], '_blank')}>预览</Button>
                          )}
                        </>
                      ) : log.content}
                    </Table.Cell>
                  </Table.Row>
                );
              })}
          </Table.Body>

          <Table.Footer>
            <Table.Row>
              <Table.HeaderCell colSpan={'10'}>
                <Button size='small' onClick={() => { setExportModal(true); setExportError(''); }}>导出消费日志</Button>
                <Button size='small' onClick={() => { setExportTopupModal(true); setExportTopupError(''); }}>导出充值日志</Button>
                <Pagination
                  floated='right'
                  activePage={activePage}
                  onPageChange={onPaginationChange}
                  size='small'
                  siblingRange={1}
                  totalPages={
                    Math.ceil(logs.length / ITEMS_PER_PAGE) +
                    (logs.length % ITEMS_PER_PAGE === 0 ? 1 : 0)
                  }
                />
              </Table.HeaderCell>
            </Table.Row>
          </Table.Footer>
        </Table>
      </Segment>

      <Modal open={exportModal} onClose={() => setExportModal(false)} size='small'>
        <Modal.Header>导出消费日志</Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Input
              label='开始时间'
              type='datetime-local'
              name='export_start'
              value={export_start}
              onChange={handleExportInputChange}
            />
            <Form.Input
              label='结束时间'
              type='datetime-local'
              name='export_end'
              value={export_end}
              onChange={handleExportInputChange}
            />
            {exportError && <p style={{ color: 'red' }}>{exportError}</p>}
            <p style={{ color: 'gray', fontSize: '0.9em' }}>最多支持导出 30 天内的消费日志，单次上限 5 万条</p>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setExportModal(false)}>取消</Button>
          <Button primary loading={exporting} onClick={handleExport}>导出</Button>
        </Modal.Actions>
      </Modal>

      <Modal open={exportTopupModal} onClose={() => setExportTopupModal(false)} size='small'>
        <Modal.Header>导出充值日志</Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Input
              label='开始时间'
              type='datetime-local'
              name='export_topup_start'
              value={export_topup_start}
              onChange={handleExportTopupInputChange}
            />
            <Form.Input
              label='结束时间'
              type='datetime-local'
              name='export_topup_end'
              value={export_topup_end}
              onChange={handleExportTopupInputChange}
            />
            {exportTopupError && <p style={{ color: 'red' }}>{exportTopupError}</p>}
            <p style={{ color: 'gray', fontSize: '0.9em' }}>最多支持导出 30 天内的充值日志，单次上限 5 万条</p>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setExportTopupModal(false)}>取消</Button>
          <Button primary loading={exportingTopup} onClick={handleExportTopup}>导出</Button>
        </Modal.Actions>
      </Modal>

      <Modal open={detailContent !== null} onClose={() => setDetailContent(null)} size='large'>
        <Modal.Header>详情</Modal.Header>
        <Modal.Content scrolling>
          <pre
            style={{
              margin: 0,
              padding: '1em',
              background: '#f7f7f7',
              borderRadius: '4px',
              fontFamily: 'monospace',
              lineHeight: 1.6,
              whiteSpace: 'pre-wrap',
              overflowWrap: 'anywhere'
            }}
          >
            {prettyDetailContent}
          </pre>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => navigator.clipboard.writeText(prettyDetailContent).then(() => showSuccess('已复制到剪贴板'))}>复制详情</Button>
          <Button onClick={() => setDetailContent(null)}>关闭</Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

export default LogsTable;
